import { useMemo } from 'react'
import {
  Figura, barraV, escala, marcasDeEixo, retangulo, tetoDoEixo,
  useDica, useLargura, type ItemLegenda,
} from './base'

/**
 * Colunas empilhadas por período — a forma da receita mês a mês.
 *
 * Por que empilhada e não agrupada: a pergunta da clínica é dupla —
 * "faturei quanto?" (a altura total) e "quanto disso já entrou?" (a
 * composição). Barras agrupadas respondem a segunda e destroem a
 * primeira: o total vira uma soma que o olho tem de fazer.
 *
 * Por que não duas linhas (recebido e a receber): a soma de duas
 * linhas não é legível, e o par recebido/pendente é parte-de-todo,
 * não duas grandezas independentes.
 *
 * Por que não um segundo eixo com o número de recebíveis: eixo duplo
 * inventa correlação. Se o número de recebíveis importa, ele é outro
 * gráfico ou uma coluna da tabela.
 *
 * A ordem da pilha é fixa e de baixo para cima: **recebido** na base
 * (é o chão, o que já é real), **a receber** em cima dele, **vencido**
 * no topo — o topo é onde o olho vai, e vencido é o que precisa de
 * ação. Entre segmentos ficam 2px da cor da superfície; não existe
 * contorno em volta de barra.
 */

export type Segmento = { chave: string; rotulo: string; cor: string }
export type Periodo = { chave: string; rotulo: string; valores: Record<string, number> }

export function ColunasEmpilhadas({
  titulo, subtitulo, periodos, segmentos, formatar, acao,
}: {
  titulo: string
  subtitulo?: string
  periodos: Periodo[]
  /** Ordem = ordem da pilha, de baixo para cima. Máximo 4. */
  segmentos: Segmento[]
  /** Como escrever um valor no eixo, na dica e na tabela. */
  formatar: (v: number) => string
  acao?: React.ReactNode
}) {
  const [caixa, largura] = useLargura<HTMLDivElement>()
  const { dica, mostrar, esconder } = useDica()

  const M = { e: 64, d: 12, c: 14, b: 30 }   // esquerda, direita, cima, baixo
  const altura = 220
  const totais = useMemo(
    () => periodos.map((p) => segmentos.reduce((s, g) => s + (p.valores[g.chave] ?? 0), 0)),
    [periodos, segmentos])
  const teto = tetoDoEixo(Math.max(0, ...totais))
  const marcas = marcasDeEixo(Math.max(0, ...totais))

  const y = escala(0, teto || 1, altura - M.b, M.c)
  const banda = periodos.length ? (largura - M.e - M.d) / periodos.length : 0
  // ≤24px é o teto do traço. A sobra da banda é ar, não barra gorda.
  const larguraBarra = Math.max(6, Math.min(24, banda * 0.55))
  const RESPIRO = 2   // os 2px de superfície entre segmentos

  const legenda: ItemLegenda[] = segmentos.map((s) => ({ rotulo: s.rotulo, cor: s.cor }))

  return (
    <Figura
      titulo={titulo} subtitulo={subtitulo} legenda={legenda} dica={dica} acao={acao}
      tabela={{
        colunas: ['Período', ...segmentos.map((s) => s.rotulo), 'Total'],
        linhas: periodos.map((p, i) => [
          p.rotulo,
          ...segmentos.map((s) => formatar(p.valores[s.chave] ?? 0)),
          formatar(totais[i] ?? 0),
        ]),
      }}>
      <div className="gfx__tela" ref={caixa}>
        {largura > 0 && (
          <svg width={largura} height={altura} role="img"
               aria-label={`${titulo}. ${periodos.length} períodos. Os valores estão na tabela abaixo do gráfico.`}>
            {/* Grade: fio de 1px, sólida, recuada. Nunca tracejada —
                tracejado lê como "projeção" ou "limite". */}
            {marcas.map((m) => (
              <g key={m}>
                <line x1={M.e} x2={largura - M.d} y1={y(m)} y2={y(m)}
                      stroke="var(--cl-viz-grade)" strokeWidth="1" />
                <text x={M.e - 8} y={y(m) + 4} textAnchor="end"
                      className="gfx__tick">{formatar(m)}</text>
              </g>
            ))}

            {periodos.map((p, i) => {
              const x = M.e + i * banda + (banda - larguraBarra) / 2
              const total = totais[i] ?? 0
              let acumulado = 0
              // O topo da pilha é o último segmento com valor: é ele
              // que ganha o canto de 4px.
              const ultimoCheio = [...segmentos].reverse()
                .find((s) => (p.valores[s.chave] ?? 0) > 0)?.chave

              return (
                <g key={p.chave}
                   onPointerEnter={() => mostrar({
                     x: x + larguraBarra / 2, y: y(total) - 8,
                     titulo: p.rotulo,
                     linhas: [
                       ...segmentos.map((s) => `${s.rotulo}: ${formatar(p.valores[s.chave] ?? 0)}`),
                       `Total: ${formatar(total)}`,
                     ],
                   })}
                   onPointerLeave={esconder}>
                  {/* Alvo de hover do tamanho da BANDA, não da barra:
                      uma coluna de 8px é impossível de acertar. */}
                  <rect x={M.e + i * banda} y={M.c} width={banda}
                        height={altura - M.c - M.b} fill="transparent" />

                  {segmentos.map((s) => {
                    const v = p.valores[s.chave] ?? 0
                    if (v <= 0) return null
                    const yTopo = y(acumulado + v)
                    const alt = y(acumulado) - yTopo - RESPIRO
                    acumulado += v
                    return (
                      <path key={s.chave} fill={s.cor}
                            d={s.chave === ultimoCheio
                              ? barraV(x, yTopo, larguraBarra, Math.max(0, alt + RESPIRO))
                              : retangulo(x, yTopo, larguraBarra, Math.max(0, alt))} />
                    )
                  })}

                  {/* Rótulo direto só quando há poucos períodos. Com
                      doze meses na tela ele vira ruído e some sob a
                      coluna vizinha. */}
                  {periodos.length <= 6 && total > 0 && (
                    <text x={x + larguraBarra / 2} y={y(total) - 7}
                          textAnchor="middle" className="gfx__valor">
                      {formatar(total)}
                    </text>
                  )}

                  <text x={M.e + i * banda + banda / 2} y={altura - 10}
                        textAnchor="middle" className="gfx__tick">{p.rotulo}</text>
                </g>
              )
            })}

            {/* Linha de base. A única linha com peso: é o zero. */}
            <line x1={M.e} x2={largura - M.d} y1={y(0)} y2={y(0)}
                  stroke="var(--cl-viz-eixo)" strokeWidth="1" />
          </svg>
        )}
      </div>
    </Figura>
  )
}
